import { useEffect, useState } from 'react';

/**
 * Custom React hook for managing local storage with state synchronization.
 *
 * This hook allows you to store, retrieve, and synchronize a value in local storage.
 * It also ensures that the state is updated whenever the value in local storage changes.
 *
 * @template T - The type of the stored value.
 *
 * @param {string} key - The key under which the value is stored in local storage.
 * @param {T} initialValue - The initial value to store if no value exists for the given key.
 *
 * @returns {[T, (value: T | ((prevValue: T) => T)) => void]} A tuple containing:
 *   - The current value stored in local storage.
 *   - A function to update the value in local storage.
 *
 * @example
 * ```typescript
 * import { useLocalStorage } from './useLocalStorage';
 *
 * const MyComponent = () => {
 *   const [name, setName] = useLocalStorage<string>('name', 'Guest');
 *
 *   const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
 *     setName(event.target.value);
 *   };
 *
 *   return (
 *     <div>
 *       <p>Welcome, {name}!</p>
 *       <input type="text" value={name} onChange={handleChange} />
 *     </div>
 *   );
 * };
 * ```
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage | MDN localStorage Documentation}
 */
export const useLocalStorage = <T>(key: string, initialValue: T): [T, (value: T) => void] => {
  const [value, setValue] = useState<T>(() => {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};
